// Weekly dispatch schedule helpers, shared by the admin editor and the buyer page.
// Shape (backend dispatchWeeklyHours): { "0".."6": [{ start: "HH:mm", end: "HH:mm" }] },
// 0 = Sunday, 30-minute steps, end may be "24:00"; a range ending before it starts
// runs past midnight. null = not set = no cards. Only the page's open hours
// 07:00–02:00 can be picked, so all day = 07:00–02:00.

/** Monday first, the way the week is shown. */
export const DAYS = [
  { key: '1', en: 'Mon', th: 'จันทร์' },
  { key: '2', en: 'Tue', th: 'อังคาร' },
  { key: '3', en: 'Wed', th: 'พุธ' },
  { key: '4', en: 'Thu', th: 'พฤหัส' },
  { key: '5', en: 'Fri', th: 'ศุกร์' },
  { key: '6', en: 'Sat', th: 'เสาร์' },
  { key: '0', en: 'Sun', th: 'อาทิตย์' },
];

const half = Array.from({ length: 48 }, (_, i) => `${String(Math.floor(i / 2)).padStart(2, '0')}:${i % 2 ? '30' : '00'}`);
// Listed in the order of the working day: 07:00 … 23:30, then after midnight.
export const START_TIMES = [...half.slice(14), ...half.slice(0, 4)]; // 07:00 … 23:30, 00:00 … 01:30
export const END_TIMES = [...half.slice(15), '24:00', ...half.slice(1, 5)]; // 07:30 … 24:00, 00:30 … 02:00

export const rangesText = (ranges) => (ranges && ranges.length
  ? ranges.map((r) => `${r.start}–${r.end}`).join(', ')
  : 'off');

export const NOT_SET = 'Not set — no cards';

const toMinutes = (hhmm) => {
  const [h, m] = hhmm.split(':').map(Number);
  return h * 60 + m;
};
const fromMinutes = (min) => (min >= 1440 ? '24:00' : `${String(Math.floor(min / 60)).padStart(2, '0')}:${min % 60 ? '30' : '00'}`);

/** Inside 07:00–02:00 (same rule as the backend): from 07:00 to 02:00 next morning, or after midnight ending by 02:00. */
export const withinOpenHours = ({ start, end }) => {
  const s = toMinutes(start);
  let e = toMinutes(end);
  if (e <= s) e += 1440;
  return (s >= 420 && e <= 1560) || (s < 120 && e <= 120);
};

/** A day's ranges overlap (same rule as the backend; a past-midnight range runs to its end the next day). */
export const rangesOverlap = (ranges) => {
  const spans = ranges
    .map(({ start, end }) => {
      const s = toMinutes(start);
      const e = toMinutes(end);
      return [s, e > s ? e : e + 1440];
    })
    .sort((a, b) => a[0] - b[0]);
  return spans.some((span, i) => i > 0 && span[0] < spans[i - 1][1]);
};

/** The range "Add" proposes: 10:00–22:00 on an empty day, else 2 h after the last range ends; null if no room left. */
export const nextRange = (ranges) => {
  if (!ranges.length) return { start: '10:00', end: '22:00' };
  const last = ranges[ranges.length - 1];
  const s = toMinutes(last.start);
  const e = toMinutes(last.end);
  if (e <= s || e >= 1440) return null; // ends at midnight or later: nothing left in the day
  return { start: last.end, end: fromMinutes(Math.min(e + 120, 1440)) };
};

/** "Every day 10:00–02:00", or "Mon–Sat 08:00–24:00 · Sun 13:00–24:00". */
export const weeklySummary = (weekly) => {
  if (!weekly) return NOT_SET;
  const groups = [];
  DAYS.forEach((d) => {
    const text = rangesText(weekly[d.key]);
    const last = groups[groups.length - 1];
    if (last && last.text === text) last.to = d.en;
    else groups.push({ from: d.en, to: d.en, text });
  });
  if (groups.length === 1) return `Every day ${groups[0].text}`;
  return groups.map((g) => `${g.from === g.to ? g.from : `${g.from}–${g.to}`} ${g.text}`).join(' · ');
};

/** Today's key ("0".."6") on the Thai clock. */
export const thaiTodayKey = () => {
  const name = new Date().toLocaleDateString('en-US', { weekday: 'short', timeZone: 'Asia/Bangkok' });
  return String(['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(name));
};

// ── Shift buyers (dispatchShifts: { "YYYY-MM-DD": ranges }) ──────────────────

const dayLabel = (date, locale) => date.toLocaleDateString(locale, {
  timeZone: 'Asia/Bangkok', weekday: 'short', day: 'numeric', month: 'short',
});

/** Today and the next 6 days on the Thai clock: [{ key: "2026-10-28", en: "Wed 28 Oct", th: "พ. 28 ต.ค." }]. */
export const nextSevenDays = () => Array.from({ length: 7 }, (_, i) => {
  const d = new Date(Date.now() + i * 86400000);
  return {
    key: d.toLocaleDateString('en-CA', { timeZone: 'Asia/Bangkok' }), // YYYY-MM-DD
    en: dayLabel(d, 'en-GB'),
    th: dayLabel(d, 'th-TH'),
  };
});

export const NO_SHIFTS = 'No shifts — no cards';

/** Upcoming shifts, e.g. "Wed 28 Oct 09:00–18:00 · Fri 30 Oct 22:00–02:00". */
export const shiftsSummary = (shifts) => {
  const parts = nextSevenDays()
    .filter((d) => shifts?.[d.key]?.length)
    .map((d) => `${d.en} ${rangesText(shifts[d.key])}`);
  return parts.length ? parts.join(' · ') : NO_SHIFTS;
};

/** First problem in a { key: ranges } map — a range into 02:00–07:00, or overlapping ranges — or null. */
export const rangesProblem = (byKey) => {
  for (const [key, ranges] of Object.entries(byKey)) {
    if (ranges.some((r) => !withinOpenHours(r))) return { key, kind: 'closed' };
    if (rangesOverlap(ranges)) return { key, kind: 'overlap' };
  }
  return null;
};
